import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import '../common/rtCommon.css';
import './ViewStaffs.css';
import tenantStorage from '../../utils/tenantStorage';

const ViewStaffs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [staffs, setStaffs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStaffs();
  }, []);

  const fetchStaffs = async () => {
    try {
      setIsLoading(true);
      const token = tenantStorage.getItem('token');
      const response = await apiGet('/staff/list', { 'Authorization': `Bearer ${token}` });
      
      if (response.status === 200) {
        setStaffs(response.data || []);
      } else {
        toastError('Error fetching staff list');
      }
    } catch (err) {
      toastError('Error fetching staff list: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (staffId) => {
    navigate(`/staffs/view/${staffId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  const formatSalary = (salary) => {
    if (!salary) return '-';
    return `₹${salary.toLocaleString()}`;
  };

  // Define table columns
  const columns = [
    {
      Header: 'Staff ID',
      accessor: 'staff_id'
    },
    {
      Header: 'Name',
      accessor: 'name'
    },
    {
      Header: 'Mobile',
      accessor: 'mobile'
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: ({ value }) => value || '-'
    },
    {
      Header: 'Job Type',
      accessor: 'job_type'
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => (
        <span 
          className={`status-pill ${value === 'Active' ? 'status-active' : 'status-inactive'}`}
        >
          {value}
        </span>
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="actions-cell">
          <button
            className="action-link view-link"
            onClick={() => handleViewDetails(row.staff_id)}
          >
            View
          </button>
        </div>
      )
    }
  ];

  const handleRowClick = (staff) => {
    handleViewDetails(staff.staff_id);
  };

  if (isLoading) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" activePath={location.pathname} />
        <div className="rt-main-content">
          <div className="rt-title-row">
            <h2 className="rt-title">View Staffs</h2>
          </div>
          <div className="rt-main-section">
            <div className="loading-container">
              <RTSpinner size="large" showMessage={true} message="Loading staff list..." />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">View Staffs</h2>
          <div className="rt-actions">
            <RTButton 
              className="back-btn" 
              variant="secondary" 
              onClick={() => navigate(-1)}
            >
              ← Back
            </RTButton>
          </div>
        </div>
        <div className="rt-main-section">
          {staffs.length === 0 ? (
            <div className="no-data-container">
              <p>No staff members found.</p>
            </div>
          ) : (
            <Table
              title="Staff Members"
              columns={columns}
              data={staffs}
              onRowClick={handleRowClick}
              showSearch={true}
              exportable={true}
              showPagination={true}
              noDataText="No staff members found."
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewStaffs;
